import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import Styles from "../lets_talk/Letstalk.module.css";
import SearchInput from "../../components/searchinput/SearchInput";
import DataTable from "../../components/datatable/DataTable";
import Pagination from "../../components/pagination/Pagination";
import moment from "moment";
import dayjs from "dayjs";
import isSameOrOrBefore from "dayjs/plugin/isSameOrBefore";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosInstance } from "../../axios/index";
import Buttons from "../../components/buttons/Buttons";
import { plus } from "../../controller/Images";
import customstyles from "./Blog.module.css";

// Extend dayjs with necessary plugins
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrOrBefore);
dayjs.extend(customParseFormat);

// Helper function to derive initial date label from URL params or applied dates
const getInitialDateLabelFromParams = (from, to) => {
  const today = dayjs().format("DD/MM/YYYY");
  const last7DaysFrom = dayjs().subtract(6, "days").format("DD/MM/YYYY");
  const last30DaysFrom = dayjs().subtract(29, "days").format("DD/MM/YYYY");
  const thisMonthStart = dayjs().startOf("month").format("DD/MM/YYYY");
  const thisMonthEnd = dayjs().endOf("month").format("DD/MM/YYYY");

  if (!from && !to) return "";

  if (from === today && to === today) return "Today";
  if (from === last7DaysFrom && to === today) return "Last 7 Days";
  if (from === last30DaysFrom && to === today) return "Last 30 Days";
  if (from === thisMonthStart && to === thisMonthEnd) return "This Month";

  if (from && to) return `${from} - ${to}`;
  if (from) return `From ${from}`;
  if (to) return `To ${to}`;
  return "";
};

const Blog = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [allBlogs, setAllBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  // Helper to initialize filters from URL params - REFACTORED TO useCallback
  const getInitialFilters = useCallback(
    () => ({
      search: searchParams.get("search") || "",
      authorName: searchParams.get("authorName") || "",
      authorDesignation: searchParams.get("authorDesignation") || "",
      categoryId: searchParams.get("categoryId") || "",
      postedByName: searchParams.get("postedByName") || "",
      fromDate: searchParams.get("fromDate") || "",
      toDate: searchParams.get("toDate") || "",
      appliedDateLabel: getInitialDateLabelFromParams(
        searchParams.get("fromDate") || "",
        searchParams.get("toDate") || "",
      ),
    }),
    [searchParams],
  );

  const initialCurrentPage = Number(searchParams.get("page")) || 1;
  const initialRowsPerPage = Number(searchParams.get("limit")) || 10;

  // Main filter state - CONSOLIDATED
  const [filters, setFilters] = useState(getInitialFilters);
  // const [appliedFilters, setAppliedFilters] = useState({}); // This state is not used in the provided code, keeping it commented for simplicity.
  const [isInitialLoad, setIsInitialLoad] = useState(true); // 🔥 NEW STATE FOR CONTROLLED FETCHING

  // Temporary states for filter popups - CONSOLIDATED
  const [tempFilters, setTempFilters] = useState({
    postedByName: "",
    authorName: "",
    authorDesignation: "",
    categoryId: "",
    fromDate: "",
    toDate: "",
    dateLabel: "",
  });

  const [chipData, setChipData] = useState([]);
  const [isOpen, setIsOpen] = useState({ status: false, column: "" });
  const filterRef = useRef(null);

  const [currentPage, setCurrentPage] = useState(initialCurrentPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const rowsPerPageOptions = [10, 15, 20, 50];

  // ... (useMemo definitions for unique authors, designations, categories remain the same)
  // Memoized unique authors for filter options
  const uniqueAuthors = useMemo(() => {
    const authors = new Set(
      allBlogs.map((blog) => blog.author_name).filter(Boolean),
    );
    return Array.from(authors).map((author) => ({
      label: author,
      value: author,
    }));
  }, [allBlogs]);

  // Memoized unique designations for filter options
  const uniqueDesignations = useMemo(() => {
    const designations = new Set(
      allBlogs.map((blog) => blog.author_designation).filter(Boolean),
    );
    return Array.from(designations).map((designation) => ({
      label: designation,
      value: designation,
    }));
  }, [allBlogs]);

  // Memoized unique categories for filter options
  const uniqueCategories = useMemo(() => {
    const categoriesMap = new Map();
    allBlogs.forEach((blog) => {
      blog.category?.forEach((cat) => {
        if (!categoriesMap.has(cat.category_id)) {
          categoriesMap.set(cat.category_id, {
            label: cat.category_name,
            value: cat.category_id.toString(),
          });
        }
      });
    });
    return Array.from(categoriesMap.values());
  }, [allBlogs]);

  // Column definitions for DataTable
  const columns = useMemo(
    () => [
      { header: "S.No", accessor: "sno" },
      { header: "Title", accessor: "title", sortable: true },
      {
        header: "Author",
        accessor: "author_name",
        sortable: true,
        filterable: true,
      },
      {
        header: "Designation",
        accessor: "author_designation",
        sortable: true,
        filterable: true,
      },
      {
        header: "Category",
        accessor: "category",
        sortable: true,
        filterable: true,
      },
      {
        header: "Posted By",
        accessor: "posted_by_name",
        sortable: true,
        filterable: true,
      },
      {
        header: "Posted Date",
        accessor: "posted_date",
        sortable: true,
        filterable: true,
        isDateColumn: true,
      },
    ],
    [],
  );

  // Filter options for DataTable
  const filterOptions = useMemo(
    () => ({
      posted_by_name: [
        { label: "Super Admin", value: "Super Admin" },
        { label: "Content Creator", value: "Content Creator" },
      ],
      author_name: uniqueAuthors,
      author_designation: uniqueDesignations,
      category: uniqueCategories,
    }),
    [uniqueAuthors, uniqueDesignations, uniqueCategories],
  );
  // ... (useMemo definitions end)

  // ----------------------------------------------------------------------
  // 🔥 FIX: Function to fetch all blogs - MODIFIED to accept filters as argument
  // ----------------------------------------------------------------------
  const fetchAllBlogs = useCallback(async (currentFilters) => {
    setLoading(true);
    try {
      const payloadFromDate = currentFilters.fromDate
        ? moment(currentFilters.fromDate, "DD/MM/YYYY").format(
            "YYYY-MM-DD HH:mm:ss",
          )
        : "";
      const payloadToDate = currentFilters.toDate
        ? moment(currentFilters.toDate, "DD/MM/YYYY").format(
            "YYYY-MM-DD HH:mm:ss",
          )
        : "";

      const res = await AxiosInstance.post("/api/blog/list", {
        blog_status: "",
        tag_id: "",
        author_name: currentFilters.authorName,
        author_designation: currentFilters.authorDesignation,
        categories_id: currentFilters.categoryId,
        posted_by_name: currentFilters.postedByName,
        search: currentFilters.search,
        start_date: payloadFromDate,
        end_date: payloadToDate,
      });

      const blogList = Array.isArray(res?.data?.blog) ? res.data.blog : [];
      setAllBlogs(blogList);
    } catch (error) {
      console.error("Error fetching all blogs:", error);
      setAllBlogs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // ----------------------------------------------------------------------
  // 🔥 FIX: Consolidated Filter Initialization and Single Initial API Call
  // ----------------------------------------------------------------------
  useEffect(() => {
    let initialLoadFilters;

    // 1. Check for filters passed from location.state (e.g., returning from View)
    if (location.state?.blogfilters) {
      initialLoadFilters = location.state.blogfilters;

      // Update filters state for display and subsequent runs
      setFilters({
        ...initialLoadFilters,
        appliedDateLabel: getInitialDateLabelFromParams(
          initialLoadFilters.fromDate || "",
          initialLoadFilters.toDate || "",
        ),
      });

      // NOTE: Clearing location.state is not strictly necessary here,
      // as we control the fetch with isInitialLoad and filters state.
      // window.history.replaceState({}, document.title); // Optional clear
    } else {
      // 2. Use filters derived from URL search params (getInitialFilters)
      initialLoadFilters = getInitialFilters();
    }

    // 3. Perform the initial API call using the determined filters
    fetchAllBlogs(initialLoadFilters);

    // 4. Mark initial load as complete
    setIsInitialLoad(false);

    // We rely on this running only on mount and location.state changes
  }, [location.state, fetchAllBlogs, getInitialFilters]);

  // ----------------------------------------------------------------------
  // 🔥 NEW: Effect to re-fetch blogs when filters are changed by user interaction
  // ----------------------------------------------------------------------
  useEffect(() => {
    // Only re-fetch if it's NOT the initial load
    if (!isInitialLoad) {
      // This handles filter changes made by user (chips, filter popups, search)
      fetchAllBlogs(filters);
    }
  }, [filters, isInitialLoad, fetchAllBlogs]);

  // Effect to generate and update filter chips - REFACTORED
  useEffect(() => {
    const currentChips = [];
    if (filters.search)
      currentChips.push({
        type: "search",
        value: filters.search,
        label: `Search: "${filters.search}"`,
      });
    if (filters.authorName)
      currentChips.push({
        label: `Author: ${filters.authorName}`,
        type: "author_name",
        value: filters.authorName,
      });
    if (filters.authorDesignation)
      currentChips.push({
        label: `Designation: ${filters.authorDesignation}`,
        type: "author_designation",
        value: filters.authorDesignation,
      });
    if (filters.categoryId) {
      const selectedCategory = uniqueCategories.find(
        (cat) => cat.value === filters.categoryId,
      );
      if (selectedCategory) {
        currentChips.push({
          label: `Category: ${selectedCategory.label}`,
          type: "category",
          value: filters.categoryId,
        });
      }
    }
    if (filters.postedByName)
      currentChips.push({
        label: `Posted By: ${filters.postedByName}`,
        type: "posted_by_name",
        value: filters.postedByName,
      });

    if (filters.fromDate || filters.toDate) {
      currentChips.push({
        label: `Posted Date: ${
          filters.appliedDateLabel ||
          getInitialDateLabelFromParams(filters.fromDate, filters.toDate)
        }`,
        type: "posted_date",
        value: `${filters.fromDate}-${filters.toDate}`,
      });
    }
    setChipData(currentChips);
  }, [filters, uniqueCategories]);

  // Effect to update URL search parameters - REFACTORED
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set("search", filters.search);
    if (filters.authorName) params.set("authorName", filters.authorName);
    if (filters.authorDesignation)
      params.set("authorDesignation", filters.authorDesignation);
    if (filters.categoryId) params.set("categoryId", filters.categoryId);
    if (filters.postedByName) params.set("postedByName", filters.postedByName);
    if (filters.fromDate) params.set("fromDate", filters.fromDate);
    if (filters.toDate) params.set("toDate", filters.toDate);
    if (currentPage !== 1) params.set("page", currentPage.toString());
    if (rowsPerPage !== 10) params.set("limit", rowsPerPage.toString());
    setSearchParams(params);
  }, [filters, currentPage, rowsPerPage, setSearchParams]);

  // Memoized filtered blogs
  const filteredBlogs = useMemo(() => {
    return allBlogs;
  }, [allBlogs]);

  // Memoized paginated blogs
  const paginatedBlogs = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage;
    return filteredBlogs.slice(start, start + rowsPerPage);
  }, [filteredBlogs, currentPage, rowsPerPage]);

  // Handle search input - REFACTORED
  const handleSearch = useCallback((val) => {
    setFilters((prev) => ({ ...prev, search: val }));
    setCurrentPage(1);
  }, []);

  // Handle deletion of individual filter chips - REFACTORED
  const handleDeleteChip = useCallback((chip) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (chip.type === "search") updated.search = "";
      if (chip.type === "author_name") updated.authorName = "";
      if (chip.type === "author_designation") updated.authorDesignation = "";
      if (chip.type === "category") updated.categoryId = "";
      if (chip.type === "posted_by_name") updated.postedByName = "";
      if (chip.type === "posted_date") {
        updated.fromDate = "";
        updated.toDate = "";
        updated.appliedDateLabel = "";
      }
      return updated;
    });

    setTempFilters({
      postedByName: "",
      authorName: "",
      authorDesignation: "",
      categoryId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setCurrentPage(1);
  }, []);

  // Handle clearing all filters - REFACTORED
  const handleAllClear = useCallback(() => {
    setFilters({
      search: "",
      authorName: "",
      authorDesignation: "",
      categoryId: "",
      postedByName: "",
      fromDate: "",
      toDate: "",
      appliedDateLabel: "",
    });

    setTempFilters({
      postedByName: "",
      authorName: "",
      authorDesignation: "",
      categoryId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setCurrentPage(1);
  }, []);

  // Handle opening filter popup - REFACTORED
  const handleFilterClick = useCallback(
    (col) => {
      setIsOpen({ status: true, column: col });
      if (col === "posted_by_name") {
        setTempFilters((prev) => ({
          ...prev,
          postedByName: filters.postedByName,
        }));
      } else if (col === "author_name") {
        setTempFilters((prev) => ({
          ...prev,
          authorName: filters.authorName,
        }));
      } else if (col === "author_designation") {
        setTempFilters((prev) => ({
          ...prev,
          authorDesignation: filters.authorDesignation,
        }));
      } else if (col === "category") {
        setTempFilters((prev) => ({
          ...prev,
          categoryId: filters.categoryId,
        }));
      } else if (col === "posted_date") {
        setTempFilters((prev) => ({
          ...prev,
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          dateLabel: filters.appliedDateLabel,
        }));
      }
    },
    [filters],
  );

  // Handle applying filter - REFACTORED (for categorical filters)
  const handleApplyFilter = useCallback(() => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (isOpen.column === "author_name") {
        updated.authorName = tempFilters.authorName;
      } else if (isOpen.column === "author_designation") {
        updated.authorDesignation = tempFilters.authorDesignation;
      } else if (isOpen.column === "category") {
        updated.categoryId = tempFilters.categoryId;
      } else if (isOpen.column === "posted_by_name") {
        updated.postedByName = tempFilters.postedByName;
      }
      return updated;
    });

    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, [isOpen.column, tempFilters]);

  // Handle clearing specific filter - REFACTORED
  const handleClearSpecificFilter = useCallback((col) => {
    setFilters((prev) => {
      const updated = { ...prev };
      if (col === "posted_by_name") updated.postedByName = "";
      else if (col === "author_name") updated.authorName = "";
      else if (col === "author_designation") updated.authorDesignation = "";
      else if (col === "category") updated.categoryId = "";
      else if (col === "posted_date") {
        updated.fromDate = "";
        updated.toDate = "";
        updated.appliedDateLabel = "";
      }
      return updated;
    });

    setTempFilters({
      postedByName: "",
      authorName: "",
      authorDesignation: "",
      categoryId: "",
      fromDate: "",
      toDate: "",
      dateLabel: "",
    });

    setIsOpen({ status: false, column: "" });
    setCurrentPage(1);
  }, []);

  // Handle date range apply - REFACTORED
  const handleDateRangeApply = useCallback(
    ({ fromDate: from, toDate: to, label, selectedLabel = "" }) => {
      setFilters((prev) => ({
        ...prev,
        fromDate: from,
        toDate: to,
        appliedDateLabel: selectedLabel || label,
      }));

      setTempFilters((prev) => ({
        ...prev,
        fromDate: from,
        toDate: to,
        dateLabel: selectedLabel || label,
      }));

      setIsOpen({ status: false, column: "" });
      setCurrentPage(1);
    },
    [],
  );

  // Handle categorical filter change - REFACTORED
  const handleCategoricalFilterChange = useCallback((column, value) => {
    const filterMap = {
      posted_by_name: "postedByName",
      author_name: "authorName",
      author_designation: "authorDesignation",
      category: "categoryId",
    };
    const filterKey = filterMap[column];
    setTempFilters((prev) => ({
      ...prev,
      [filterKey]: value,
    }));
  }, []);

  // Handle clicks outside filter popup - REFACTORED
  const handleClickOutside = useCallback(
    (e) => {
      if (
        filterRef.current &&
        !filterRef.current.contains(e.target) &&
        !e.target.closest('img[alt="filter"]')
      ) {
        setIsOpen((prev) => {
          if (prev.status) {
            setTempFilters({
              postedByName: filters.postedByName,
              authorName: filters.authorName,
              authorDesignation: filters.authorDesignation,
              categoryId: filters.categoryId,
              fromDate: filters.fromDate,
              toDate: filters.toDate,
              dateLabel: filters.appliedDateLabel,
            });
          }
          return { ...prev, status: false };
        });
      }
    },
    [filters],
  );

  // Add and remove event listener for clicks outside
  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [handleClickOutside]);

  // Processed blogs for DataTable rendering
  const processedBlogs = useMemo(
    () =>
      paginatedBlogs.map((blog, i) => ({
        sno: (currentPage - 1) * rowsPerPage + i + 1,
        id: blog.id || "-",
        title: blog.title || "-",
        author_name: blog.author_name || "-",
        author_designation: blog.author_designation || "-",
        category:
          blog.category?.map((cat) => cat.category_name).join(", ") || "-",
        posted_by_name: blog.posted_by_name || "-",
        posted_date: blog.posted_date
          ? moment(blog.posted_date).format("DD/MM/YYYY")
          : "-",
      })),
    [paginatedBlogs, currentPage, rowsPerPage],
  );

  // Handle view click for blog details
  const handleViewClick = useCallback(
    (row) => {
      navigate(`/admin/Blog-View/${row.id}`, {
        state: {
          blogfilters: filters, // include it inside state
        },
      });
    },
    [navigate, filters],
  );

  // Action icon properties for DataTable
  const actionIconProps = useMemo(
    () => ({
      defaultIcon: "",
      hoverIcon: "",
      onClick: handleViewClick,
    }),
    [handleViewClick],
  );

  // Handle creating new blog
  const handleCreateblogClick = () => {
    navigate("/admin/create-blog");
  };

  return (
    <div className={`container-fluid px-3 ${Styles.main_Container}`}>
      <div className={`row mb-3 ${customstyles.blogHeaderRow}`}>
        <div className={`d-flex align-items-center ${customstyles.searchCol}`}>
          <SearchInput
            handleKeyDown={handleSearch}
            placeholder="Search with Title, Author, Tagline..."
            value={filters.search}
          />
        </div>
        <div className={`d-flex align-items-center ${customstyles.buttonCol}`}>
          <Buttons
            text="Create New Blog"
            type="button"
            variant="secondary"
            icon={
              <img
                src={plus}
                alt="plus"
                style={{ width: "25px", height: "25px" }}
              />
            }
            onClick={handleCreateblogClick}
          />
        </div>
      </div>

      {/* Filter Chips Display */}
      <div className="mb-3">
        {chipData.map((chip, i) => (
          <span
            key={i}
            className="custom-badge me-2 rounded-md py-2 px-3 flex items-center space-x-2"
          >
            {chip.label}
            <button
              onClick={() => handleDeleteChip(chip)}
              className="btn-close ms-2"
            ></button>
          </span>
        ))}
        {chipData.length > 0 && (
          <button
            className="btn btn-sm btn-outline-danger ms-2 rounded-md py-2 px-3"
            onClick={handleAllClear}
          >
            Clear All Filters
          </button>
        )}
      </div>

      {/* DataTable Component */}
      <DataTable
        columns={columns}
        loading={loading}
        data={processedBlogs}
        filterOptions={filterOptions}
        filters={{
          posted_by_name: filters.postedByName,
          author_name: filters.authorName,
          author_designation: filters.authorDesignation,
          category: filters.categoryId,
          posted_date_from: filters.fromDate,
          posted_date_to: filters.toDate,
        }}
        actionIcon={actionIconProps}
        handleFilter={handleFilterClick}
        isOpens={isOpen}
        filterPopupRef={filterRef}
        currentDateRange={
          isOpen.status && isOpen.column === "posted_date"
            ? {
                fromDate: tempFilters.fromDate,
                toDate: tempFilters.toDate,
                label: tempFilters.dateLabel,
              }
            : null
        }
        onDateRangeApply={handleDateRangeApply}
        tempCategoricalFilters={{
          posted_by_name: tempFilters.postedByName,
          author_name: tempFilters.authorName,
          author_designation: tempFilters.authorDesignation,
          category: tempFilters.categoryId,
        }}
        onCategoricalFilterChange={handleCategoricalFilterChange}
        handleApplyFilter={handleApplyFilter}
        onClearSpecificFilter={handleClearSpecificFilter}
        ellipsisIndex={[1, 2, 4]}
      />

      {/* Pagination Component */}
      <div className="mb-3 d-flex justify-content-end">
        <Pagination
          currentPage={currentPage}
          rowsPerPage={rowsPerPage}
          totalItems={filteredBlogs.length}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={setRowsPerPage}
          rowsPerPageOptions={rowsPerPageOptions}
        />
      </div>
    </div>
  );
};

export default Blog;
